import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { writeFocusSession } from '../FocusWidget';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  getExecutionSummary,
  generateExecutionSummary,
  addNodeUpdate,
  fetchPlanningTodos,
  createPlanningTodo,
  updatePlanningTodo,
  deletePlanningTodo,
  generateTodosFromNodes,
  createThinkingDoc,
} from '../../actions/planningAction';
import './PlanningExecute.css';

// ── Shared helpers ────────────────────────────────────────────────────────────

export function todayDateKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function focusLsKey(visionId) {
  return `dmyor_focus_${visionId}_${todayDateKey()}`;
}

// ── Date helpers ──────────────────────────────────────────────────────────────

const MS_IN_DAY = 1000 * 60 * 60 * 24;

function startOfDay(d) {
  const x = new Date(d); x.setHours(0, 0, 0, 0); return x;
}

function dateKeyOf(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dateBucket(dueISO) {
  if (!dueISO) return 'anytime';
  const diff = Math.round((startOfDay(new Date(dueISO)) - startOfDay(new Date())) / MS_IN_DAY);
  if (diff < 0)   return 'overdue';
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff <= 7)  return 'thisWeek';
  return 'later';
}

const BUCKET_META = {
  tomorrow: { label: 'Tomorrow',  order: 0 },
  thisWeek: { label: 'This week', order: 1 },
  later:    { label: 'Later',     order: 2 },
};

function isoFromDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDue(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Flatten milestones ────────────────────────────────────────────────────────

function flattenNodes(milestones) {
  const out = [];
  for (const m of milestones) {
    out.push({ ...m, _crumb: null });
    for (const a of (m.actions || [])) {
      out.push({ ...a, _crumb: m.content });
      for (const s of (a.subNodes || [])) out.push({ ...s, _crumb: a.content });
    }
    for (const s of (m.subNodes || [])) out.push({ ...s, _crumb: m.content });
  }
  return out;
}

function nodeLabel(n) {
  return n.customType || (n.type === 'milestone' ? 'Milestone' : n.type === 'action' ? 'Action' : 'Node');
}

// ── Progress ring ─────────────────────────────────────────────────────────────

function ProgressRing({ done, total }) {
  const R = 25;
  const C = 2 * Math.PI * R;
  const pct = total > 0 ? Math.min(1, done / total) : 0;
  const complete = total > 0 && done >= total;
  return (
    <div className={`pe-ring${complete ? ' pe-ring--complete' : ''}`} title={`${done} of ${total} done today`}>
      <svg viewBox="0 0 60 60" width="60" height="60">
        <circle className="pe-ring-track" cx="30" cy="30" r={R} />
        <circle
          className="pe-ring-fill"
          cx="30" cy="30" r={R}
          strokeDasharray={C}
          strokeDashoffset={C * (1 - pct)}
        />
      </svg>
      <div className="pe-ring-center">
        {total > 0 ? (
          <>
            <span className="pe-ring-num">{done}</span>
            <span className="pe-ring-den">/{total}</span>
          </>
        ) : (
          <span className="pe-ring-dash">–</span>
        )}
      </div>
    </div>
  );
}

// ── Node picker modal ─────────────────────────────────────────────────────────

function NodePicker({ allNodes, currentNodeId, onPick, onClose }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const lower = q.toLowerCase();
    return allNodes.filter(n => !lower || (n.content || '').toLowerCase().includes(lower));
  }, [allNodes, q]);

  return (
    <div className="pe-np-overlay" onClick={onClose}>
      <div className="pe-np-panel" onClick={e => e.stopPropagation()}>
        <div className="pe-np-head">
          <p className="pe-np-title">Link to a node</p>
          <button className="pe-np-close" onClick={onClose}>✕</button>
        </div>
        <input
          className="pe-np-search"
          placeholder="Search nodes…"
          value={q}
          onChange={e => setQ(e.target.value)}
          autoFocus
        />
        <ul className="pe-np-list">
          {currentNodeId && (
            <li>
              <button className="pe-np-none" onClick={() => onPick(null, null)}>
                Remove link
              </button>
            </li>
          )}
          {filtered.length === 0 && (
            <li className="pe-np-empty">No nodes found.</li>
          )}
          {filtered.map(n => (
            <li key={String(n._id)}>
              <button
                className={`pe-np-item${String(n._id) === String(currentNodeId) ? ' pe-np-item--active' : ''}`}
                onClick={() => onPick(String(n._id), (n.content || '').slice(0, 80))}
              >
                <span className={`pe-np-tag pe-np-tag--${n.type}`}>{nodeLabel(n)}</span>
                <span className="pe-np-content">{n.content}</span>
                {n._crumb && <span className="pe-np-crumb">↳ {n._crumb}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Todo row ──────────────────────────────────────────────────────────────────

function TodoRow({
  todo, bucket, onToggle, onDelete, onOpenLog, onOpenNodePicker,
  onGoToNode, logOpenFor, onToggleFocus, focusFull, onRescheduleToday, onSetDue,
  onThink, onOpenDoc, highlighted, crumbText, onStartFocus, focusActiveTodoId,
}) {
  const isLogOpen = logOpenFor === String(todo._id);
  const canLog    = todo.done && todo.nodeId;
  const starDisabled = !todo.isFocus && focusFull;
  const dueRef = useRef(null);

  const openDuePicker = () => {
    const el = dueRef.current;
    if (!el) return;
    if (el.showPicker) el.showPicker(); else el.click();
  };

  return (
    <li
      id={`pe-todo-${todo._id}`}
      className={`pe-item${todo._pending ? ' pe-item--pending' : ''}${todo.done ? ' pe-item--done' : ''}${todo.isFocus ? ' pe-item--focus' : ''}${highlighted ? ' pe-item--highlight' : ''}`}
    >
      <button
        className={`pe-checkbox${todo.done ? ' pe-checkbox--checked' : ''}`}
        onClick={onToggle}
        title={todo.done ? 'Unmark' : 'Mark done'}
      >
        {todo.done && <span>✓</span>}
      </button>

      <div className="pe-item-body">
        <span className="pe-item-text">{todo.text}</span>
        {(todo.nodeId || crumbText) ? (
          <span className="pe-item-crumb">
            ↳{' '}
            <button className="pe-node-goto-btn" onClick={onGoToNode} title="Go to node">
              {crumbText
                ? `${crumbText.slice(0, 80)}${crumbText.length > 80 ? '…' : ''}`
                : 'Linked node'}
            </button>
            <button className="pe-node-change-btn" onClick={onOpenNodePicker} title="Change node">✎</button>
          </span>
        ) : (
          <button className="pe-node-link-btn" onClick={onOpenNodePicker}>+ Link node</button>
        )}
      </div>

      <div className="pe-item-meta">
        {((todo.due && bucket !== 'overdue') || !todo.due) && !todo.done && (
          <span className="pe-due-trigger">
            {todo.due ? (
              <button
                className={`pe-due pe-due--${bucket || 'later'} pe-due--btn`}
                onClick={openDuePicker}
                title="Change due date"
              >{formatDue(todo.due)}</button>
            ) : (
              <button className="pe-due-add" onClick={openDuePicker} title="Set due date">
                <i className="bx bx-calendar-plus" />
              </button>
            )}
            {/* Real, tappable date input overlaying the trigger — opens the
                native picker reliably on mobile (where showPicker/click on a
                zero-size hidden input does not). */}
            <input
              ref={dueRef}
              type="date"
              className="pe-due-overlay"
              value={todo.due || ''}
              onChange={e => onSetDue(e.target.value)}
              title="Set due date"
            />
          </span>
        )}

        {bucket === 'overdue' && !todo.done && (
          <button className="pe-resched-btn" onClick={onRescheduleToday} title="Move to today">
            → Today
          </button>
        )}

        {canLog && (
          <button
            className={`pe-log-btn${isLogOpen ? ' pe-log-btn--active' : ''}`}
            onClick={onOpenLog}
          >
            {isLogOpen ? 'Cancel' : 'Log to plan'}
          </button>
        )}


        {!todo.archivedAt && (
          todo.resourceId ? (
            <button
              className="pe-think-btn pe-think-btn--has-doc"
              onClick={onOpenDoc}
              title="Open thinking doc"
            ><i className="bx bxs-file-doc" /></button>
          ) : (
            <button
              className="pe-think-btn"
              onClick={onThink}
              title="Think this through in a doc"
            ><i className="bx bx-bulb" /></button>
          )
        )}

        {!todo.archivedAt && (
          <button
            className={`pe-star-btn${todo.isFocus ? ' pe-star-btn--on' : ''}`}
            onClick={onToggleFocus}
            disabled={starDisabled}
            title={todo.isFocus ? 'Remove from focus' : starDisabled ? 'Focus is full (3 max)' : 'Add to today’s focus'}
          >★</button>
        )}

        {!todo.done && !todo.archivedAt && (
          <button
            className={`pe-focus-bolt${focusActiveTodoId === String(todo._id) ? ' pe-focus-bolt--on' : ''}${todo.focusStatus === 'failed' ? ' pe-focus-bolt--failed' : ''}`}
            onClick={() => onStartFocus(todo)}
            title={todo.focusStatus === 'failed' ? 'Last attempt failed — try again' : 'Focus mode'}
            disabled={focusActiveTodoId && focusActiveTodoId !== String(todo._id)}
          ><i className="bx bxs-bolt" /></button>
        )}

        <button className="pe-item-remove" onClick={onDelete} title="Delete"><i className="bx bx-trash" /></button>
      </div>
    </li>
  );
}

// ── Update composer ───────────────────────────────────────────────────────────

function UpdateComposer({ todo, onPost, onClose, posting }) {
  const [text, setText] = useState('');
  return (
    <li className="pe-composer-wrap">
      <p className="pe-composer-label">
        Log progress on: <strong>{todo.nodeContent || 'node'}{(todo.nodeContent?.length || 0) >= 80 ? '…' : ''}</strong>
      </p>
      <textarea
        className="pe-composer-input"
        placeholder="What happened? What did you finish or learn?"
        value={text}
        onChange={e => setText(e.target.value)}
        autoFocus
        rows={3}
      />
      <div className="pe-composer-actions">
        <button className="pe-composer-cancel" onClick={onClose} disabled={posting}>Cancel</button>
        <button
          className="pe-composer-post"
          onClick={() => onPost(text)}
          disabled={posting || !text.trim()}
        >
          {posting ? 'Posting…' : 'Post update'}
        </button>
      </div>
    </li>
  );
}

// ── Pinned node row ───────────────────────────────────────────────────────────

function PinnedNodeRow({ node, onUnpin, onJump }) {
  const status = node.status || 'standby';
  return (
    <li className={`pe-item pe-item--node pe-item--${status}`} onClick={() => onJump?.(node._id)}>
      <span className={`pe-node-dot pe-node-dot--${status}`} />
      <div className="pe-item-body">
        {node._crumb && <span className="pe-item-crumb">{node._crumb}</span>}
        <span className="pe-item-text">{node.content}</span>
      </div>
      <div className="pe-item-meta">
        <span className={`pe-node-tag pe-node-tag--${node.type}`}>{nodeLabel(node)}</span>
        <button
          className="pe-item-unpin"
          onClick={e => { e.stopPropagation(); onUnpin(node._id); }}
          title="Unpin"
        >★</button>
      </div>
    </li>
  );
}

// ── Milestone momentum ────────────────────────────────────────────────────────

function MomentumPanel({ milestones, onFocusNode }) {
  const rows = useMemo(() => (milestones || []).map(m => {
    const acts  = m.actions || [];
    const done  = acts.filter(a => a.status === 'done').length;
    const total = acts.length;
    const pct   = total > 0 ? Math.round((done / total) * 100) : (m.status === 'done' ? 100 : 0);
    return { id: String(m._id), label: m.content, done, total, pct, status: m.status || 'standby' };
  }), [milestones]);

  if (rows.length === 0) return null;

  return (
    <section className="pe-section pe-momentum">
      <h2 className="pe-section-title">Milestone momentum</h2>
      <ul className="pe-momentum-list">
        {rows.map(r => (
          <li key={r.id}>
            <button className="pe-momentum-row" onClick={() => onFocusNode?.(r.id)} title="Open in planner">
              <div className="pe-momentum-top">
                <span className="pe-momentum-label">{r.label}</span>
                <span className="pe-momentum-count">
                  {r.total > 0 ? `${r.done}/${r.total}` : '—'}
                </span>
              </div>
              <div className="pe-momentum-bar">
                <div
                  className={`pe-momentum-fill${r.pct >= 100 ? ' pe-momentum-fill--done' : ''}`}
                  style={{ width: `${r.pct}%` }}
                />
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Plan summary ──────────────────────────────────────────────────────────────

// Render markdown links of the form [title](node:<id>) as clickable node jumps.
function summaryMdComponents(onFocusNode) {
  return {
    a: ({ href, children }) => {
      if (href && href.startsWith('node:')) {
        const id = href.slice(5);
        return (
          <button
            type="button"
            className="pe-summary-nodelink"
            onClick={() => onFocusNode?.(id)}
            title="Open in planner"
          >{children}</button>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
    },
  };
}

function PlanSummary({ visionId, onFocusNode }) {
  const dispatch  = useDispatch();
  const [open,    setOpen]    = useState(false);
  const [summary, setSummary] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (!open || loaded.current) return;
    loaded.current = true;
    dispatch(getExecutionSummary(visionId))
      .then(d => { setSummary(d.summary || ''); setUpdatedAt(d.updatedAt); })
      .catch(() => {});
  }, [open, visionId, dispatch]);

  const generate = async () => {
    setLoading(true);
    try {
      const d = await dispatch(generateExecutionSummary(visionId));
      setSummary(d.summary || ''); setUpdatedAt(d.updatedAt);
    } catch (_) {}
    setLoading(false);
  };

  const stamp = updatedAt
    ? new Date(updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;

  return (
    <div className="pe-summary-wrap">
      <div className="pe-summary-header">
        <button className="pe-summary-toggle" onClick={() => setOpen(o => !o)}>
          <span>✦ Summary</span>
          <span className="pe-summary-caret">{open ? '▴' : '▾'}</span>
        </button>
        <button
          className="pe-summary-regen"
          onClick={generate}
          disabled={loading}
          title={summary ? 'Regenerate summary' : 'Generate summary'}
        >
          {loading ? 'Thinking…' : summary ? 'Regenerate' : 'Generate'}
        </button>
      </div>
      {open && (
        <div className="pe-summary-body">
          {summary ? (
            <>
              <div className="pe-summary-md">
                <ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={(url) => url} components={summaryMdComponents(onFocusNode)}>{summary}</ReactMarkdown>
              </div>
              {stamp && <span className="pe-summary-stamp">Generated {stamp}</span>}
            </>
          ) : (
            <div className="pe-summary-empty">
              <p>See where your vision stands — what's working, what's stuck, what to do next.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Scratchpad ────────────────────────────────────────────────────────────────

function ScratchPad({ visionId }) {
  const key = `dmyor_scratch_${visionId}`;
  const [text,  setText]  = useState(() => localStorage.getItem(key) || '');
  const [idle,  setIdle]  = useState(false);
  const saveTimer = useRef(null);
  const idleTimer = useRef(null);

  const onChange = e => {
    const val = e.target.value;
    setText(val);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => localStorage.setItem(key, val), 400);
  };

  useEffect(() => {
    const resetIdle = () => {
      setIdle(false);
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => setIdle(true), 10000);
    };
    const events = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetIdle, { passive: true }));
    resetIdle();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdle));
      clearTimeout(idleTimer.current);
      clearTimeout(saveTimer.current);
    };
  }, []);

  return (
    <div className={`pe-scratch${idle ? ' pe-scratch--idle' : ''}`}>
      <textarea
        className="pe-scratch-input"
        placeholder="Quick thoughts, reminders, anything…"
        value={text}
        onChange={onChange}
        rows={5}
      />
      {idle && (
        <div className="pe-eyes" aria-hidden="true">
          <span className="pe-eye"><span className="pe-eye-pupil" /></span>
          <span className="pe-eye"><span className="pe-eye-pupil" /></span>
        </div>
      )}
    </div>
  );
}

// ── AI todos panel ────────────────────────────────────────────────────────────

function AiTodosPanel({ allNodes, visionId, onAdd, onClose, dispatch }) {
  const [step,     setStep]     = useState('select'); // 'select' | 'loading' | 'review'
  const [selected, setSelected] = useState(new Set());
  const [suggestions, setSuggestions] = useState([]);
  const [kept,     setKept]     = useState(new Set());
  const [adding,   setAdding]   = useState(false);
  const [error,    setError]    = useState('');
  const [autoMode, setAutoMode] = useState(false);

  const toggleNode = id => {
    setSelected(p => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const generate = async (auto = false) => {
    if (!auto && selected.size === 0) return;
    setAutoMode(auto);
    setStep('loading');
    setError('');
    try {
      const nodeIds = auto ? [] : [...selected];
      const d = await dispatch(generateTodosFromNodes(visionId, nodeIds));
      const suggs = (d.todos || []).map((t, i) => ({ ...t, _key: `s_${i}` }));
      setSuggestions(suggs);
      setKept(new Set(suggs.map(t => t._key)));
      setStep('review');
    } catch (e) {
      setError('Failed to generate. Try again.');
      setStep('select');
    }
  };

  const toggleKept = key => {
    setKept(p => {
      const n = new Set(p);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  const addSelected = () => {
    const toAdd = suggestions.filter(t => kept.has(t._key));
    if (!toAdd.length) return;
    onClose();
    onAdd(toAdd);
  };

  return (
    <div className="pe-ai-overlay" onClick={onClose}>
      <div className="pe-ai-panel" onClick={e => e.stopPropagation()}>
        <div className="pe-ai-head">
          <div>
            <p className="pe-ai-title">✦ Dmyor Assist</p>
            {step === 'select' && <p className="pe-ai-sub">Which milestones do you want to work on?</p>}
            {step === 'review' && <p className="pe-ai-sub">{autoMode ? 'Dmyor Assist picked these — ' : ''}Choose which todos to add.</p>}
          </div>
          <button className="pe-ai-close" onClick={onClose}>✕</button>
        </div>

        <div className="pe-ai-body">
          {step === 'select' && (
            <>
              {error && <p className="pe-ai-error">{error}</p>}
              <ul className="pe-ai-node-list">
                {allNodes.map(n => {
                  const id = String(n._id);
                  const sel = selected.has(id);
                  return (
                    <li key={id}>
                      <button
                        className={`pe-ai-node-item${sel ? ' pe-ai-node-item--sel' : ''}`}
                        onClick={() => toggleNode(id)}
                      >
                        <span className="pe-ai-node-content">{n.content}</span>
                        <span className={`pe-ai-node-check${sel ? ' pe-ai-node-check--on' : ''}`}>
                          {sel ? '✓' : ''}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {step === 'loading' && (
            <div className="pe-ai-loading">
              <div className="pe-ai-spinner" />
              <p>Generating todos from {selected.size} node{selected.size > 1 ? 's' : ''}…</p>
            </div>
          )}

          {step === 'review' && (
            <ul className="pe-ai-sugg-list">
              {suggestions.map(t => {
                const on = kept.has(t._key);
                return (
                  <li key={t._key}>
                    <button
                      className={`pe-ai-sugg-item${on ? ' pe-ai-sugg-item--on' : ''}`}
                      onClick={() => toggleKept(t._key)}
                    >
                      <span className={`pe-ai-node-check${on ? ' pe-ai-node-check--on' : ''}`}>
                        {on ? '✓' : ''}
                      </span>
                      <div className="pe-ai-sugg-body">
                        {t.nodeContent && (
                          <span className="pe-ai-sugg-node">
                            ↳ {t.nodeContent.length > 60 ? t.nodeContent.slice(0, 60) + '…' : t.nodeContent}
                          </span>
                        )}
                        <span className="pe-ai-sugg-text">{t.text}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="pe-ai-foot">
          {step === 'select' && (
            <>
              <button
                className="pe-ai-auto-btn"
                onClick={() => generate(true)}
              >
                ✦ Let Dmyor Assist pick
              </button>
              <span className="pe-ai-foot-divider">or</span>
              <span className="pe-ai-sel-count">{selected.size} selected</span>
              <button
                className="pe-ai-generate-btn"
                onClick={() => generate(false)}
                disabled={selected.size === 0}
              >
                Generate
              </button>
            </>
          )}
          {step === 'review' && (
            <>
              <button className="pe-ai-back-btn" onClick={() => setStep('select')}>Back</button>
              <span className="pe-ai-sel-count">{kept.size} todo{kept.size !== 1 ? 's' : ''} selected</span>
              <button
                className="pe-ai-generate-btn"
                onClick={addSelected}
                disabled={kept.size === 0}
              >
                Add {kept.size}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Collapsible section header ────────────────────────────────────────────────

function SectionHeader({ title, count, tone, open, onToggle, action }) {
  return (
    <div className={`pe-sec-head${tone ? ` pe-sec-head--${tone}` : ''}`}>
      <button className="pe-sec-toggle" onClick={onToggle} disabled={!onToggle}>
        <span className="pe-sec-title">{title}</span>
        {count > 0 && <span className="pe-sec-count">{count}</span>}
        {onToggle && <span className="pe-sec-caret">{open ? '▴' : '▾'}</span>}
      </button>
      {action}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

const DUE_PRESETS = [
  { label: 'Today',    days: 0 },
  { label: 'Tomorrow', days: 1 },
];

const MAX_FOCUS = 3;

// ── Focus mode: time picker ────────────────────────────────────────────────────

const FOCUS_PRESETS = [15, 25, 30, 45, 60];

function FocusTimePicker({ todo, onStart, onClose }) {
  const [mins, setMins] = useState(25);
  const [custom, setCustom] = useState('');
  const chosen = custom ? parseInt(custom, 10) : mins;
  return (
    <div className="pe-focus-overlay" onMouseDown={onClose}>
      <div className="pe-focus-picker" onMouseDown={e => e.stopPropagation()}>
        <div className="pe-focus-picker-title">⚡ Focus mode</div>
        <div className="pe-focus-picker-todo">{todo.text.length > 70 ? todo.text.slice(0, 70) + '…' : todo.text}</div>
        <div className="pe-focus-picker-label">Set a timer</div>
        <div className="pe-focus-presets">
          {FOCUS_PRESETS.map(p => (
            <button
              key={p}
              className={`pe-focus-preset${!custom && mins === p ? ' pe-focus-preset--on' : ''}`}
              onClick={() => { setMins(p); setCustom(''); }}
            >{p} min</button>
          ))}
        </div>
        <input
          className="pe-focus-custom"
          type="number"
          min="1" max="240"
          placeholder="Custom (mins)"
          value={custom}
          onChange={e => setCustom(e.target.value)}
        />
        <div className="pe-focus-picker-actions">
          <button className="pe-focus-cancel" onClick={onClose}>Cancel</button>
          <button
            className="pe-focus-start"
            disabled={!chosen || chosen < 1}
            onClick={() => onStart(chosen)}
          >Start</button>
        </div>
      </div>
    </div>
  );
}


export default function PlanningExecute({ vision, milestones, visionId, onFocusNode, onRefresh, theme, focusTodo, externalRefreshKey }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lsKey = focusLsKey(visionId);

  // Thinking-doc: which todo is pending the "create a doc?" confirm
  const [thinkTodo,  setThinkTodo]  = useState(null);
  const [thinkBusy,  setThinkBusy]  = useState(false);
  // Highlight a todo when deep-linked from its doc (?todo=<id>)
  const [highlightTodoId, setHighlightTodoId] = useState(null);

  const [pinnedIds,    setPinnedIds]    = useState(() => JSON.parse(localStorage.getItem(lsKey) || '[]'));
  const [todos,        setTodos]        = useState([]);
  const [todosLoading, setTodosLoading] = useState(true);

  // Add form
  const [newText,       setNewText]       = useState('');
  const [newDue,        setNewDue]        = useState('');
  const [newNodeId,     setNewNodeId]     = useState(null);
  const [newNodeContent,setNewNodeContent]= useState(null);
  const [adding,        setAdding]        = useState(false);
  const dateInputRef = useRef(null);

  // Node picker: null | 'new' | todo._id string
  const [pickerCtx, setPickerCtx] = useState(null);

  // AI panel
  const [showAi,      setShowAi]      = useState(false);
  const [aiProgress,  setAiProgress]  = useState(null); // null | { current, total }

  // Focus mode
  const [focusPickerTodo, setFocusPickerTodo] = useState(null); // todo being picked for focus
  const focusActiveTodo = useMemo(
    () => todos.find(t => t.focusStatus === 'active' && t.focusStartedAt) || null,
    [todos]
  );

  // Collapsibles
  const [upcomingOpen, setUpcomingOpen] = useState(false);
  const [anytimeOpen,  setAnytimeOpen]  = useState(true);
  const [doneOpen,     setDoneOpen]     = useState(true);
  const [historyOpen,  setHistoryOpen]  = useState(false);

  const [logOpenFor,    setLogOpenFor]    = useState(null);
  const [postingUpdate, setPostingUpdate] = useState(false);

  // Load todos
  useEffect(() => {
    let cancelled = false;
    setTodosLoading(true);
    dispatch(fetchPlanningTodos(visionId))
      .then(d => { if (!cancelled) setTodos(d.todos || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setTodosLoading(false); });
    return () => { cancelled = true; };
  }, [visionId, dispatch, externalRefreshKey]);

  useEffect(() => {
    const sync = () => setPinnedIds(JSON.parse(localStorage.getItem(lsKey) || '[]'));
    window.addEventListener('focus', sync);
    return () => window.removeEventListener('focus', sync);
  }, [lsKey]);

  // Sync local todos when widget clears or extends the focus session externally
  useEffect(() => {
    const onFocusUpdate = (e) => {
      if (e.detail === null) {
        setTodos(p => p.map(t =>
          t.focusStatus === 'active'
            ? { ...t, focusStartedAt: null, focusDurationMins: null, focusStatus: null }
            : t
        ));
      } else if (e.detail?.todoId) {
        setTodos(p => p.map(t =>
          String(t._id) === e.detail.todoId
            ? { ...t, focusDurationMins: e.detail.focusDurationMins }
            : t
        ));
      }
    };
    window.addEventListener('dmyor-focus-update', onFocusUpdate);
    return () => window.removeEventListener('dmyor-focus-update', onFocusUpdate);
  }, []);

  useEffect(() => { localStorage.setItem(lsKey, JSON.stringify(pinnedIds)); }, [pinnedIds, lsKey]);

  const allNodes    = useMemo(() => flattenNodes(milestones), [milestones]);
  const nodeMap     = useMemo(() => Object.fromEntries(allNodes.map(n => [String(n._id), n])), [allNodes]);
  const pinnedNodes = useMemo(() => pinnedIds.map(id => nodeMap[id]).filter(Boolean), [pinnedIds, nodeMap]);

  // ── Todo partitions ──
  // Focus section holds starred todos (done ones stay visible as wins until cleared).
  const focusTodos   = useMemo(() => todos.filter(t => t.isFocus && !t.archivedAt), [todos]);
  const activeTodos  = useMemo(() => todos.filter(t => !t.done && !t.archivedAt && !t.isFocus), [todos]);
  const doneTodos    = useMemo(() => todos.filter(t =>  t.done && !t.archivedAt && !t.isFocus), [todos]);
  const historyTodos = useMemo(() => [...todos.filter(t => t.archivedAt)].sort((a, b) => new Date(b.archivedAt) - new Date(a.archivedAt)), [todos]);

  const byBucket = useMemo(() => {
    const map = { overdue: [], today: [], tomorrow: [], thisWeek: [], later: [], anytime: [] };
    activeTodos.forEach(t => map[dateBucket(t.due)].push(t));
    return map;
  }, [activeTodos]);

  const upcomingGroups = useMemo(() =>
    ['tomorrow', 'thisWeek', 'later']
      .map(k => [k, byBucket[k]])
      .filter(([, list]) => list.length > 0)
      .sort(([a], [b]) => BUCKET_META[a].order - BUCKET_META[b].order),
  [byBucket]);

  const upcomingCount = upcomingGroups.reduce((s, [, l]) => s + l.length, 0);

  // ── Day stats ──
  const todayKey = todayDateKey();

  const doneTodayCount = useMemo(
    () => todos.filter(t => t.completedAt && dateKeyOf(t.completedAt) === todayKey).length,
    [todos, todayKey]
  );

  const ringTotal = useMemo(() => {
    const slate = todos.filter(t =>
      !t.done && !t.archivedAt &&
      (t.isFocus || ['overdue', 'today'].includes(dateBucket(t.due)))
    ).length;
    return doneTodayCount + slate;
  }, [todos, doneTodayCount]);

  const focusFull = focusTodos.length >= MAX_FOCUS;

  // ── Node picker handler ──

  const handleNodePick = useCallback(async (nodeId, nodeContent) => {
    setPickerCtx(null);
    if (pickerCtx === 'new') {
      setNewNodeId(nodeId);
      setNewNodeContent(nodeContent);
      return;
    }
    const todo = todos.find(t => String(t._id) === pickerCtx);
    if (!todo) return;
    const patch = { nodeId: nodeId || null, nodeContent: nodeContent || null };
    setTodos(p => p.map(t => t._id === todo._id ? { ...t, ...patch } : t));
    try {
      const d = await dispatch(updatePlanningTodo(String(todo._id), patch));
      setTodos(p => p.map(t => t._id === todo._id ? d.todo : t));
    } catch (_) {
      setTodos(p => p.map(t => t._id === todo._id ? todo : t));
    }
  }, [pickerCtx, todos, dispatch]);

  // ── CRUD ──

  const patchTodo = useCallback(async (todo, patch) => {
    setTodos(p => p.map(t => t._id === todo._id ? { ...t, ...patch } : t));
    try {
      const d = await dispatch(updatePlanningTodo(String(todo._id), patch));
      setTodos(p => p.map(t => t._id === todo._id ? d.todo : t));
    } catch (_) {
      setTodos(p => p.map(t => t._id === todo._id ? todo : t));
    }
  }, [dispatch]);

  const addTodo = async () => {
    const text = newText.trim();
    if (!text || adding) return;
    setAdding(true);
    const due     = newDue;
    const nodeId  = newNodeId;
    const nodeCnt = newNodeContent;
    setNewText(''); setNewDue(''); setNewNodeId(null); setNewNodeContent(null);
    try {
      const d = await dispatch(createPlanningTodo(visionId, {
        text,
        due:         due || null,
        nodeId:      nodeId || undefined,
        nodeContent: nodeCnt || undefined,
      }));
      setTodos(p => [...p, d.todo]);
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.info(err.response.data?.message || 'Upgrade to Pro for unlimited todos.');
        navigate('/dashboard/upgrade');
        setNewText(text); // restore what they typed
      } else {
        toast.error('Could not add todo.');
      }
    }
    setAdding(false);
  };

  const toggleDone = useCallback((todo) => {
    const done = !todo.done;
    return patchTodo(todo, { done, completedAt: done ? new Date().toISOString() : null });
  }, [patchTodo]);

  const archiveTodo = useCallback((todo) => {
    return patchTodo(todo, { archivedAt: new Date().toISOString(), isFocus: false });
  }, [patchTodo]);

  const toggleFocus = useCallback((todo) => {
    if (!todo.isFocus && focusTodos.length >= MAX_FOCUS) return;
    return patchTodo(todo, { isFocus: !todo.isFocus });
  }, [patchTodo, focusTodos.length]);

  // ── Focus mode handlers ──────────────────────────────────────────────────────
  // Focus Mode is a Pro feature; the server returns 402 for free users.
  const startFocus = useCallback(async (todo, durationMins) => {
    setFocusPickerTodo(null);
    const focusStartedAt = new Date().toISOString();
    const patch = { focusStartedAt, focusDurationMins: durationMins, focusStatus: 'active' };
    setTodos(p => p.map(t => t._id === todo._id ? { ...t, ...patch } : t));
    writeFocusSession({
      todoId: String(todo._id),
      visionId,
      nodeId: todo.nodeId ? String(todo.nodeId) : null,
      text: todo.text,
      focusStartedAt,
      focusDurationMins: durationMins,
    });
    try {
      const d = await dispatch(updatePlanningTodo(String(todo._id), patch));
      setTodos(p => p.map(t => t._id === todo._id ? d.todo : t));
    } catch (err) {
      writeFocusSession(null);
      setTodos(p => p.map(t => t._id === todo._id ? todo : t));
      if (err?.response?.status === 402) {
        toast.info('Focus Mode is a Pro feature. Upgrade to use it.');
        navigate('/dashboard/upgrade');
      } else {
        toast.error('Could not start focus session.');
      }
    }
  }, [dispatch, navigate, visionId]);

  const rescheduleToday = useCallback((todo) => {
    return patchTodo(todo, { due: isoFromDays(0) });
  }, [patchTodo]);

  const setDue = useCallback((todo, iso) => {
    return patchTodo(todo, { due: iso || null });
  }, [patchTodo]);

  const removeTodo = useCallback(async (todo) => {
    setTodos(p => p.filter(t => t._id !== todo._id));
    try { await dispatch(deletePlanningTodo(String(todo._id))); }
    catch (_) { setTodos(p => [...p, todo]); }
  }, [dispatch]);



  const unpin = useCallback(nodeId => {
    setPinnedIds(p => p.filter(id => id !== String(nodeId)));
  }, []);

  const addAiTodos = async (suggestions) => {
    const ts = Date.now();
    const pending = suggestions.map((s, i) => ({
      _id:         `pending_${ts}_${i}`,
      _pending:    true,
      text:        s.text,
      nodeId:      s.nodeId      || null,
      nodeContent: s.nodeContent || null,
      done: false, archivedAt: null, isFocus: false,
    }));
    setTodos(p => [...p, ...pending]);
    setAiProgress({ current: 0, total: suggestions.length });

    for (let i = 0; i < suggestions.length; i++) {
      const s   = suggestions[i];
      const pid = pending[i]._id;
      setAiProgress({ current: i + 1, total: suggestions.length });
      let confirmed = false;
      // First attempt — with nodeId
      try {
        const d = await dispatch(createPlanningTodo(visionId, {
          text:        s.text,
          nodeId:      s.nodeId      || undefined,
          nodeContent: s.nodeContent || undefined,
        }));
        setTodos(p => p.map(t => t._id === pid ? d.todo : t));
        confirmed = true;
      } catch (err) {
        console.warn('[addAiTodos] attempt 1 failed:', err?.response?.data?.message || err?.message);
      }
      // Retry without nodeId if first failed
      if (!confirmed) {
        try {
          const d = await dispatch(createPlanningTodo(visionId, {
            text:        s.text,
            nodeContent: s.nodeContent || undefined,
          }));
          setTodos(p => p.map(t => t._id === pid ? d.todo : t));
          confirmed = true;
        } catch (err2) {
          console.error('[addAiTodos] retry failed:', err2?.response?.data?.message || err2?.message);
          setTodos(p => p.filter(t => t._id !== pid));
        }
      }
    }
    setAiProgress(null);
  };

  const postUpdate = async (todo, text) => {
    if (!text.trim() || !todo.nodeId) return;
    setPostingUpdate(true);
    try {
      await dispatch(addNodeUpdate(todo.nodeId, text.trim()));
      onRefresh?.();
      await archiveTodo(todo);
      setLogOpenFor(null);
    } catch (_) {}
    setPostingUpdate(false);
  };

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const openDoc = useCallback((todo) => {
    if (todo?.resourceId) navigate(`/dashboard/planning/${visionId}/doc/${todo.resourceId}`);
  }, [navigate, visionId]);

  const confirmThink = useCallback(async () => {
    if (!thinkTodo || thinkBusy) return;
    setThinkBusy(true);
    try {
      const d = await dispatch(createThinkingDoc(visionId, thinkTodo._id));
      const resId = d?.resource?._id;
      if (resId) {
        // Flip the bulb to a doc icon immediately
        setTodos(prev => prev.map(t => String(t._id) === String(thinkTodo._id) ? { ...t, resourceId: resId } : t));
        setThinkTodo(null);
        navigate(`/dashboard/planning/${visionId}/doc/${resId}`);
      }
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Could not create the document. Try again.');
    } finally {
      setThinkBusy(false);
    }
  }, [thinkTodo, thinkBusy, dispatch, visionId, navigate]);

  // Deep-link highlight: scroll to + flash the todo when arriving from its doc.
  useEffect(() => {
    if (!focusTodo?.id || todosLoading) return;
    const idStr = String(focusTodo.id);
    setHighlightTodoId(idStr);
    const scrollT = setTimeout(() => {
      document.getElementById(`pe-todo-${idStr}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    const clearT = setTimeout(() => setHighlightTodoId(null), 2800);
    return () => { clearTimeout(scrollT); clearTimeout(clearT); };
  }, [focusTodo, todosLoading]);

  const renderTodoRow = (todo, bucket) => {
    const id = String(todo._id);
    return (
      <React.Fragment key={id}>
        <TodoRow
          todo={todo}
          bucket={bucket}
          logOpenFor={logOpenFor}
          focusFull={focusFull}
          highlighted={highlightTodoId === id}
          crumbText={todo.nodeContent || (todo.nodeId ? nodeMap[String(todo.nodeId)]?.content : null) || null}
          onToggle={() => toggleDone(todo)}
          onDelete={() => removeTodo(todo)}
          onOpenLog={() => setLogOpenFor(logOpenFor === id ? null : id)}
          onOpenNodePicker={() => setPickerCtx(id)}
          onGoToNode={() => todo.nodeId && onFocusNode?.(todo.nodeId)}
          onToggleFocus={() => toggleFocus(todo)}
          onRescheduleToday={() => rescheduleToday(todo)}
          onSetDue={iso => setDue(todo, iso)}
          onThink={() => setThinkTodo(todo)}
          onOpenDoc={() => openDoc(todo)}
          onStartFocus={(t) => setFocusPickerTodo(t)}
          focusActiveTodoId={focusActiveTodo ? String(focusActiveTodo._id) : null}
        />
        {logOpenFor === id && (
          <UpdateComposer
            todo={todo}
            onPost={text => postUpdate(todo, text)}
            onClose={() => setLogOpenFor(null)}
            posting={postingUpdate}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <div className={`pe-wrap${theme === 'light' ? ' pe-wrap--light' : ''}`}>
      {focusPickerTodo && (
        <FocusTimePicker
          todo={focusPickerTodo}
          onStart={(mins) => startFocus(focusPickerTodo, mins)}
          onClose={() => setFocusPickerTodo(null)}
        />
      )}
      {thinkTodo && (
        <div className="pe-think-overlay" onMouseDown={() => !thinkBusy && setThinkTodo(null)}>
          <div className="pe-think-modal" onMouseDown={e => e.stopPropagation()}>
            <div className="pe-think-modal-icon"><i className="bx bx-bulb" /></div>
            <h3 className="pe-think-modal-title">Think this through in a doc?</h3>
            <p className="pe-think-modal-text">
              We'll create a thinking document for
              <strong> "{thinkTodo.text.length > 70 ? thinkTodo.text.slice(0, 70) + '…' : thinkTodo.text}"</strong>
              {thinkTodo.nodeContent ? ', linked to its node and this todo.' : ', linked to this todo.'} You can write, plan, or let Dmyor Assist draft it.
            </p>
            <div className="pe-think-modal-actions">
              <button className="pe-think-cancel" onClick={() => setThinkTodo(null)} disabled={thinkBusy}>Cancel</button>
              <button className="pe-think-go" onClick={confirmThink} disabled={thinkBusy}>
                {thinkBusy ? 'Creating…' : 'Yes, create doc'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pe-inner">

        {aiProgress && (
          <div className="pe-ai-progress-bar">
            <span className="pe-ai-progress-dot" />
            Adding {aiProgress.current}/{aiProgress.total} todos…
          </div>
        )}

        {/* ── Header: today cockpit ── */}
        <div className="pe-header">
          <div className="pe-header-main">
            <h1 className="pe-heading">{today}</h1>
            {vision?.title && (
              <p className="pe-vision-name">
                <span className="pe-vision-badge">✦ {vision.title}</span>
              </p>
            )}
          </div>
          <div className="pe-header-pulse">
            <div className="pe-pulse-block">
              <ProgressRing done={doneTodayCount} total={ringTotal} />
              <span className="pe-pulse-label">today</span>
            </div>
          </div>
        </div>

        <div className="pe-columns">
        {/* ── LEFT: execution ── */}
        <div className="pe-col pe-col--left">

        {/* Quick thoughts — mobile only, shown above Today's focus */}
        <div className="pe-scratch-mobile">
          <ScratchPad visionId={visionId} />
        </div>

        {/* Focus */}
        <section className="pe-focus-section">
          <div className="pe-focus-header">
            <div className="pe-focus-title-row">
              <span className="pe-focus-label">Today's focus</span>
              <span className="pe-focus-slots">
                {Array.from({ length: MAX_FOCUS }).map((_, i) => (
                  <span key={i} className={`pe-focus-slot${i < focusTodos.length ? ' pe-focus-slot--filled' : ''}`} />
                ))}
              </span>
            </div>
            {focusTodos.length === 0 && (
              <p className="pe-focus-hint">Star up to {MAX_FOCUS} todos below — your non-negotiables for today.</p>
            )}
          </div>
          {focusTodos.length > 0 && (
            <ul className="pe-list pe-focus-list">
              {focusTodos.map(t => renderTodoRow(t, dateBucket(t.due)))}
            </ul>
          )}
        </section>

        {/* Add form */}
        <div className="pe-add-wrap">
          <div className="pe-add-row">
            <input
              className="pe-add-input"
              type="text"
              placeholder="Add a todo…"
              value={newText}
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTodo(); } }}
              maxLength={200}
              disabled={adding}
            />
            <button className="pe-add-btn" onClick={addTodo} disabled={!newText.trim() || adding}>
              {adding ? '…' : 'Add'}
            </button>
          </div>

          <div className="pe-add-controls">
            {/* Due */}
            <div className="pe-due-row">
              <span className="pe-due-label">Due</span>
              <div className="pe-due-pills">
                {DUE_PRESETS.map(p => {
                  const iso = isoFromDays(p.days);
                  const isActive = newDue === iso;
                  return (
                    <button
                      key={p.label}
                      className={`pe-due-pill${isActive ? ' pe-due-pill--active' : ''}`}
                      onClick={() => setNewDue(isActive ? '' : iso)}
                    >
                      {p.label}
                    </button>
                  );
                })}
                <span className="pe-due-trigger">
                  <button
                    className={`pe-due-pill${newDue && !DUE_PRESETS.some(p => newDue === isoFromDays(p.days)) ? ' pe-due-pill--active' : ''}`}
                    onClick={() => { if (dateInputRef.current?.showPicker) dateInputRef.current.showPicker(); else dateInputRef.current?.click(); }}
                  >
                    {newDue && !DUE_PRESETS.some(p => newDue === isoFromDays(p.days)) ? formatDue(newDue) : 'Pick date'}
                  </button>
                  <input
                    ref={dateInputRef}
                    type="date"
                    className="pe-due-overlay"
                    value={newDue}
                    onChange={e => setNewDue(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    title="Pick date"
                  />
                </span>
                {newDue && (
                  <button className="pe-due-clear" onClick={() => setNewDue('')}>×</button>
                )}
              </div>
            </div>

            {/* Node link */}
            <div className="pe-due-row">
              <span className="pe-due-label">Node</span>
              <button
                className={`pe-due-pill${newNodeId ? ' pe-due-pill--active' : ''}`}
                onClick={() => setPickerCtx('new')}
              >
                {newNodeId && newNodeContent
                  ? newNodeContent.slice(0, 28) + (newNodeContent.length > 28 ? '…' : '')
                  : '+ Link node'}
              </button>
              {newNodeId && (
                <button className="pe-due-clear" onClick={() => { setNewNodeId(null); setNewNodeContent(null); }}>×</button>
              )}
              <button className="pe-ai-trigger" onClick={() => setShowAi(true)}>
                ✦ Dmyor Assist
              </button>
            </div>
          </div>
        </div>

        {todosLoading ? (
          <p className="pe-loading">Loading…</p>
        ) : (
          <>
            {/* Overdue */}
            {byBucket.overdue.length > 0 && (
              <section className="pe-section">
                <SectionHeader title="Overdue" count={byBucket.overdue.length} tone="overdue" />
                <ul className="pe-list">
                  {byBucket.overdue.map(t => renderTodoRow(t, 'overdue'))}
                </ul>
              </section>
            )}

            {/* Today */}
            <section className="pe-section">
              <SectionHeader title="Today" count={byBucket.today.length} />
              {byBucket.today.length === 0 ? (
                <div className="pe-empty">
                  <p className="pe-empty-title">Nothing due today.</p>
                  <p className="pe-empty-sub">Add a todo above, or pull one in from Upcoming.</p>
                </div>
              ) : (
                <ul className="pe-list">
                  {byBucket.today.map(t => renderTodoRow(t, 'today'))}
                </ul>
              )}
            </section>

            {/* Anytime */}
            {byBucket.anytime.length > 0 && (
              <section className="pe-section">
                <SectionHeader
                  title="Anytime"
                  count={byBucket.anytime.length}
                  open={anytimeOpen}
                  onToggle={() => setAnytimeOpen(o => !o)}
                />
                {anytimeOpen && (
                  <ul className="pe-list">
                    {byBucket.anytime.map(t => renderTodoRow(t, 'anytime'))}
                  </ul>
                )}
              </section>
            )}

            {/* Upcoming */}
            {upcomingCount > 0 && (
              <section className="pe-section">
                <SectionHeader
                  title="Upcoming"
                  count={upcomingCount}
                  open={upcomingOpen}
                  onToggle={() => setUpcomingOpen(o => !o)}
                />
                {upcomingOpen && (
                  <ul className="pe-list">
                    {upcomingGroups.map(([bucket, list]) => (
                      <React.Fragment key={bucket}>
                        <li className="pe-bucket-label">{BUCKET_META[bucket].label}</li>
                        {list.map(t => renderTodoRow(t, bucket))}
                      </React.Fragment>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {/* Done */}
            {doneTodos.length > 0 && (
              <section className="pe-section">
                <SectionHeader
                  title="Done"
                  count={doneTodos.length}
                  open={doneOpen}
                  onToggle={() => setDoneOpen(o => !o)}
                  action={null}
                />
                {doneOpen && (
                  <ul className="pe-list">
                    {doneTodos.map(t => renderTodoRow(t, null))}
                  </ul>
                )}
              </section>
            )}

            {/* History */}
            {historyTodos.length > 0 && (
              <section className="pe-section pe-section--history">
                <SectionHeader
                  title="History"
                  count={historyTodos.length}
                  open={historyOpen}
                  onToggle={() => setHistoryOpen(o => !o)}
                />
                {historyOpen && (
                  <ul className="pe-list">
                    {historyTodos.map(t => (
                      <li key={String(t._id)} className="pe-item pe-item--history">
                        <span className="pe-history-check" />
                        <div className="pe-item-body">
                          <span className="pe-item-text">{t.text}</span>
                          {t.nodeContent && (
                            <span className="pe-item-crumb">
                              ↳{' '}
                              <button
                                className="pe-node-goto-btn"
                                onClick={() => t.nodeId && onFocusNode?.(t.nodeId)}
                                title="Go to node"
                              >
                                {t.nodeContent}{t.nodeContent.length >= 80 ? '…' : ''}
                              </button>
                            </span>
                          )}
                        </div>
                        <div className="pe-item-meta">
                          <span className="pe-history-date">{formatDue(t.archivedAt)}</span>
                          <button className="pe-item-remove" onClick={() => removeTodo(t)} title="Delete"><i className="bx bx-trash" /></button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </>
        )}

        {/* Milestone momentum — mobile only, after todos */}
        <div className="pe-momentum-mobile">
          <MomentumPanel milestones={milestones} onFocusNode={onFocusNode} />
        </div>

        </div>{/* end pe-col--left */}

        {/* ── RIGHT: context ── */}
        <div className="pe-col pe-col--right">

          {/* Scratchpad — desktop only (mobile copy sits at top of left col) */}
          <div className="pe-scratch-desktop">
            <ScratchPad visionId={visionId} />
          </div>

          {/* Pinned nodes */}
          <section className="pe-section">
            <h2 className="pe-section-title">Pinned nodes</h2>
            {pinnedNodes.length === 0 ? (
              <p className="pe-pin-hint">★ Star a node in the Planner to pin it here.</p>
            ) : (
              <ul className="pe-list">
                {pinnedNodes.map(n => (
                  <PinnedNodeRow key={n._id} node={n} onUnpin={unpin} onJump={onFocusNode} />
                ))}
              </ul>
            )}
          </section>

          {/* Milestone momentum — desktop only */}
          <div className="pe-momentum-desktop">
            <MomentumPanel milestones={milestones} onFocusNode={onFocusNode} />
          </div>

          {/* Summary */}
          <PlanSummary visionId={visionId} onFocusNode={onFocusNode} />

        </div>{/* end pe-col--right */}

        </div>{/* end pe-columns */}

      </div>

      {/* AI todos panel */}
      {showAi && (
        <AiTodosPanel
          allNodes={milestones}
          visionId={visionId}
          onAdd={addAiTodos}
          onClose={() => setShowAi(false)}
          dispatch={dispatch}
        />
      )}

      {/* Node picker modal */}
      {pickerCtx !== null && (
        <NodePicker
          allNodes={allNodes}
          currentNodeId={
            pickerCtx === 'new'
              ? newNodeId
              : (todos.find(t => String(t._id) === pickerCtx)?.nodeId || null)
          }
          onPick={handleNodePick}
          onClose={() => setPickerCtx(null)}
        />
      )}
    </div>
  );
}
